import { useSelector } from 'react-redux'
import { Redirect, Route, Switch } from 'react-router'

import { ProtectedRoute } from '@webapp/app/components'
import { makeSelectFeatureSwitches } from '@webapp/core/stores/global'
import { useFeatureFlag } from '@webapp/hooks'
import { useAppRoutes } from '@webapp/hooks/useAppRoutes'
import { IntegrationServicePlatforms } from '@webapp/models/enums'
import { APIApplicationPage } from '@webapp/pages/APIApplicationPage'
import { Dynamics365BusinessCentralPage } from '@webapp/pages/Dynamics365BusinessCentralPage'
import { IntegrationMergePage } from '@webapp/pages/IntegrationMergePage'
import { IntegrationNetsuitePage } from '@webapp/pages/IntegrationNetsuitePage'
import { IntegrationPlatformListPage } from '@webapp/pages/IntegrationPlatformListPage'
import { NetsuitePage } from '@webapp/pages/NetsuitePage'
import { QboPage } from '@webapp/pages/QboPage'
import { SageIntacctPage } from '@webapp/pages/SageIntacctPage'
import { useGetIntegrationServiceState } from '@webapp/platform/_unmigrated/hooks/useGetIntegrationServiceState'
import { Permissions } from '@webapp/platform/iam'
import { IntegrationNames } from '@webapp/platform/integrations/entities/enums/IntegrationNameEnum'
import { IntegrationDetailsPage } from '@webapp/platform/integrations/pages/IntegrationDetailsPage/IntegrationDetailsPage'
import { QuickBooksDesktopPage } from '@webapp/platform/integrations/pages/QuickBooksDesktopPage'
import {
  QuickBooksDesktopFieldMappingModels,
  QuickBooksDesktopImportStatuses,
  QuickBooksDesktopRouteParams,
  QuickBooksDesktopSettingsTabs,
} from '@webapp/platform/integrations/pages/QuickBooksDesktopPage/types'
import { SlackIntegrationPage } from '@webapp/platform/integrations/pages/SlackIntegrationPage'
import { getCurrentBusinessCentralEnvironment } from '@webapp/utils/integration/getCurrentBusinessCentralEnvironment'
import { getCurrentNetSuiteEnvironment } from '@webapp/utils/integration/getCurrentNetSuiteEnvironment'
import { getCurrentSageEnvironment } from '@webapp/utils/integration/getCurrentSageEnvironment'

const IntegrationsRoute = ({ path }) => {
  return (
    <ProtectedRoute
      exact
      path={path}
      component={IntegrationPlatformListPage}
      permissions={[Permissions.SETTINGS_MANAGE_INTEGRATION]}
    />
  )
}

export const IntegrationsRouter = () => {
  const { url, brp } = useAppRoutes()

  const PSERV_346_BASE_MIGRATION = useFeatureFlag('PSERV_346_BASE_MIGRATION')

  const { api_application_setup: showApplicationSetup } = useSelector(
    makeSelectFeatureSwitches()
  )

  const PLAT_2155_MERGE_NETSUITE = useFeatureFlag('PLAT_2155_MERGE_NETSUITE')
  const PLAT_5164_MERGE_SAGE = useFeatureFlag('PLAT_5164_MERGE_SAGE')
  const PLAT_5595_MERGE_BUSINESS_CENTRAL = useFeatureFlag(
    'PLAT_5595_MERGE_BUSINESS_CENTRAL'
  )

  const { currentQboEnvironment, data } = useGetIntegrationServiceState()
  const hasMergeQboIntegration =
    currentQboEnvironment ===
    IntegrationServicePlatforms.MERGE_QUICKBOOKS_ONLINE

  const currentNetSuiteEnvironment = PLAT_2155_MERGE_NETSUITE
    ? getCurrentNetSuiteEnvironment(data)
    : IntegrationServicePlatforms.NETSUITE

  const hasMergeNetSuiteIntegration = PLAT_2155_MERGE_NETSUITE
    ? currentNetSuiteEnvironment === IntegrationServicePlatforms.MERGE_NETSUITE
    : false

  const currentSageEnvironment = PLAT_5164_MERGE_SAGE
    ? getCurrentSageEnvironment(data)
    : IntegrationServicePlatforms.SAGE_INTACCT

  const hasMergeSageIntegration = PLAT_5164_MERGE_SAGE
    ? currentSageEnvironment === IntegrationServicePlatforms.MERGE_SAGE_INTACCT
    : false

  const currentBusinessCentralEnvironment = PLAT_5595_MERGE_BUSINESS_CENTRAL
    ? getCurrentBusinessCentralEnvironment(data)
    : IntegrationServicePlatforms.DYNAMICS_365_BUSINESS_CENTRAL

  const hasMergeBusinessCentralIntegration = PLAT_5595_MERGE_BUSINESS_CENTRAL
    ? currentBusinessCentralEnvironment ===
      IntegrationServicePlatforms.MERGE_DYNAMICS_365_BUSINESS_CENTRAL
    : false

  return (
    <Switch>
      <Route
        exact
        path={brp('NetsuitePage')}
        component={IntegrationNetsuitePage}
      />

      <Route
        path={brp('NetsuiteBillsPage')}
        render={(props) => {
          return hasMergeNetSuiteIntegration ? (
            <IntegrationDetailsPage
              platform={IntegrationNames.MERGE_NETSUITE}
            />
          ) : (
            <NetsuitePage {...props} />
          )
        }}
      />

      <Route
        path={brp('QboPage')}
        component={hasMergeQboIntegration ? IntegrationMergePage : QboPage}
      />

      <Route
        path={brp('SageIntacctPage')}
        render={(props) => {
          return hasMergeSageIntegration ? (
            <IntegrationDetailsPage
              platform={IntegrationNames.MERGE_SAGE_INTACCT}
            />
          ) : (
            <SageIntacctPage {...props} />
          )
        }}
      />

      <Route
        path={brp('Dynamics365BCPage')}
        render={(props) => {
          return hasMergeBusinessCentralIntegration ? (
            <IntegrationDetailsPage
              platform={IntegrationNames.MERGE_BUSINESS_CENTRAL}
            />
          ) : (
            <Dynamics365BusinessCentralPage {...props} />
          )
        }}
      />

      {showApplicationSetup && (
        <ProtectedRoute
          path={brp('ProcurifyApiPage')}
          component={APIApplicationPage}
          navigationPermissions={['settings']}
        />
      )}

      <ProtectedRoute
        exact
        path={brp('SlackIntegrationPage')}
        component={SlackIntegrationPage}
        permissions={[Permissions.SETTINGS_MANAGE_INTEGRATION]}
      />

      {/* PSERV_346: flag-off falls back to the Angular QuickBooks Desktop page. */}
      {!PSERV_346_BASE_MIGRATION && (
        <Redirect
          from='/integrations/quickbooks'
          to={url('LegacyQuickBooksDesktopPage')}
        />
      )}

      <ProtectedRoute
        exact
        path={brp('QuickBooksDesktopSettingsPage', {
          tab: QuickBooksDesktopRouteParams.SETTINGS_TAB,
        })}
        component={QuickBooksDesktopPage}
        permissions={[Permissions.SETTINGS_MANAGE_INTEGRATION]}
        featureFlags={['PSERV_346_BASE_MIGRATION']}
      />
      <ProtectedRoute
        exact
        path={brp('QuickBooksDesktopFieldMappingPage', {
          model: QuickBooksDesktopRouteParams.FIELD_MAPPING_MODEL,
        })}
        component={QuickBooksDesktopPage}
        permissions={[Permissions.SETTINGS_MANAGE_INTEGRATION]}
        featureFlags={['PSERV_346_BASE_MIGRATION']}
      />
      <ProtectedRoute
        exact
        path={brp('QuickBooksDesktopAccountsPage', {
          status: QuickBooksDesktopRouteParams.IMPORT_STATUS,
        })}
        component={QuickBooksDesktopPage}
        permissions={[Permissions.SETTINGS_MANAGE_INTEGRATION]}
        featureFlags={['PSERV_346_BASE_MIGRATION']}
      />
      <ProtectedRoute
        exact
        path={brp('QuickBooksDesktopVendorsPage', {
          status: QuickBooksDesktopRouteParams.IMPORT_STATUS,
        })}
        component={QuickBooksDesktopPage}
        permissions={[Permissions.SETTINGS_MANAGE_INTEGRATION]}
        featureFlags={['PSERV_346_BASE_MIGRATION']}
      />
      <ProtectedRoute
        exact
        path={brp('QuickBooksDesktopExportsPage', {
          resource: QuickBooksDesktopRouteParams.EXPORT_RESOURCE,
          status: QuickBooksDesktopRouteParams.EXPORT_STATUS,
        })}
        component={QuickBooksDesktopPage}
        permissions={[Permissions.SETTINGS_MANAGE_INTEGRATION]}
        featureFlags={['PSERV_346_BASE_MIGRATION']}
      />

      {/* Bare or unknown segments fall back to each tab's default
          (registered AFTER the constrained routes; field mapping before the
          settings fallback that would otherwise swallow it). */}
      <Redirect
        from={brp('QuickBooksDesktopFieldMappingPage', { model: ':model?' })}
        to={url('QuickBooksDesktopFieldMappingPage', {
          model: QuickBooksDesktopFieldMappingModels.PO,
        })}
      />
      <Redirect
        from={brp('QuickBooksDesktopSettingsPage', { tab: ':tab?' })}
        to={url('QuickBooksDesktopSettingsPage', {
          tab: QuickBooksDesktopSettingsTabs.GENERAL,
        })}
      />
      <Redirect
        from={brp('QuickBooksDesktopAccountsPage', { status: ':status?' })}
        to={url('QuickBooksDesktopAccountsPage', {
          status: QuickBooksDesktopImportStatuses.UNIMPORTED,
        })}
      />
      <Redirect
        from={brp('QuickBooksDesktopVendorsPage', { status: ':status?' })}
        to={url('QuickBooksDesktopVendorsPage', {
          status: QuickBooksDesktopImportStatuses.UNIMPORTED,
        })}
      />
      <Redirect
        from={brp('QuickBooksDesktopExportsPage', {
          resource: QuickBooksDesktopRouteParams.EXPORT_RESOURCE,
          status: ':status?',
        })}
        to='/integrations/quickbooks/:resource/exportable'
      />
      {/* The base route and any unknown QuickBooks desktop sub-route. */}
      <Redirect
        from='/integrations/quickbooks'
        to={url('QuickBooksDesktopSettingsPage', {
          tab: QuickBooksDesktopSettingsTabs.GENERAL,
        })}
      />

      <IntegrationsRoute path={brp('IntegrationsPage')} />
    </Switch>
  )
}
